import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PROJECT_TYPE_TEMPLATE_SET, PROPOSAL_SET_VALUES } from "../../store/constants/proposalConstants";
import { Button, ButtonGroup, Dropdown, Modal, Form } from "react-bootstrap";
import { aiTemplate } from "../../store/actions/proposals/proposals.actions";
import { FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaListUl, FaListOl, FaUndo, FaRedo, FaFont, FaPlus, FaMinus, FaRobot } from 'react-icons/fa';
import CustomPromptModal from './CustomPromptModal';

const SimpleRichTextEditor = ({ disable_fields }) => {
  const proposalValues = useSelector((state) => state.proposalValues);
  const { projectTypes } = useSelector((state) => state.projectTypeList);
  const { loading: proposalLoading } = useSelector((state) => state.proposalDetail);
  const { loading: templatesLoading } = useSelector((state) => state.proposalTemplates);
  const { loading, template, error } = useSelector((state) => state.aiTemplate);
  const { projectTypes: userProjectTypes } = useSelector(
    (state) => state.userProjectTypeList
  );
  const sections = useSelector((state) => state.proposalSections);

  const [currentSection, setCurrentSection] = useState('');
  const [editorContent, setEditorContent] = useState(proposalValues?.user_template || '');
  const [windowCleaningContent, setWindowCleaningContent] = useState(proposalValues?.user_window_cleaning || '');
  const [pressureWashingContent, setPressureWashingContent] = useState(proposalValues?.user_template_pressure_wash || '');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [canWindowUndo, setCanWindowUndo] = useState(false);
  const [canWindowRedo, setCanWindowRedo] = useState(false);
  const [canPressureUndo, setCanPressureUndo] = useState(false);
  const [canPressureRedo, setCanPressureRedo] = useState(false);
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);
  const [templateHistory, setTemplateHistory] = useState({
    main: [],
    window_cleaning: [],
    pressure_wash: []
  });
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState({
    main: 0,
    window_cleaning: 0,
    pressure_wash: 0
  });

  const dispatch = useDispatch();

  const editorRef = useRef(null);
  const windowCleaningRef = useRef(null);
  const pressureWashRef = useRef(null);
  const [selection, setSelection] = useState(null);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      setSelection(selection.getRangeAt(0));
    }
  };

  const restoreSelection = () => {
    if (selection) {
      const newSelection = window.getSelection();
      newSelection.removeAllRanges();
      newSelection.addRange(selection);
    }
  };

  const handleEditorChange = (event) => {
    if (editorRef.current) {
      const newContent = event.target.innerHTML;
      setEditorContent(newContent);
      dispatch({
        type: PROPOSAL_SET_VALUES,
        payload: { user_template: newContent }
      });
      checkUndoRedo(editorRef, setCanUndo, setCanRedo);
    }
  };

  const handleWindowCleaningChange = (event) => {
    if (windowCleaningRef.current) {
      const newContent = event.target.innerHTML;
      setWindowCleaningContent(newContent);
      dispatch({
        type: PROPOSAL_SET_VALUES,
        payload: { user_window_cleaning: newContent }
      });
      checkUndoRedo(windowCleaningRef, setCanWindowUndo, setCanWindowRedo);
    }
  };

  const handlePressureWashChange = (event) => {
    if (pressureWashRef.current) {
      const newContent = event.target.innerHTML;
      setPressureWashingContent(newContent);
      dispatch({
        type: PROPOSAL_SET_VALUES,
        payload: { user_template_pressure_wash: newContent }
      });
      checkUndoRedo(pressureWashRef, setCanPressureUndo, setCanPressureRedo);
    }
  };

  const handleCustomPrompt = (section = 'main') => {
    setCurrentSection(section);
    setShowCustomPrompt(true);
  };

  const handleSaveCustomPrompt = () => {
    const data = {
      project_name: proposalValues?.project_name,
      description: proposalValues?.project_description,
      size: proposalValues?.project_size,
      units: proposalValues?.project_units,
      stage: proposalValues?.project_stage,
      address: proposalValues?.project_address,
      project_type: proposalValues?.project_type,
      section: currentSection,
      custom_prompt: customPrompt
    }
    dispatch(aiTemplate(data));
    setShowCustomPrompt(false);
    setCustomPrompt('');
  };

  const handleGenerateAiTemplate = (section = 'main') => {
    const data = {
      project_name: proposalValues?.project_name,
      description: proposalValues?.project_description,
      size: proposalValues?.project_size,
      units: proposalValues?.project_units,
      stage: proposalValues?.project_stage,
      address: proposalValues?.project_address,
      project_type: proposalValues?.project_type,
      section: section,
      index: promptIndex % 2
    }
    setCurrentSection(section);
    dispatch(aiTemplate(data));
    if (section == 'main') {
      setPromptIndex(promptIndex + 1);
    }
  };

  const addToHistory = (section, content) => {
    setTemplateHistory(prev => {
      const newHistory = { ...prev };
      newHistory[section] = newHistory[section].slice(0, currentHistoryIndex[section] + 1);
      newHistory[section] = [...newHistory[section], content];
      return newHistory;
    });
    setCurrentHistoryIndex(prev => ({
      ...prev,
      [section]: prev[section] + 1
    }));

    // Also dispatch to Redux to persist history
    dispatch({
      type: PROPOSAL_SET_VALUES,
      payload: {
        [`${section}_history`]: templateHistory[section],
        [`${section}_history_index`]: currentHistoryIndex[section] + 1
      }
    });
  };

  const handleTemplateUndo = (section) => {
    if (currentHistoryIndex[section] > 0 && !disable_fields) {
      const newIndex = currentHistoryIndex[section] - 1;
      const previousContent = templateHistory[section][newIndex];
      
      setCurrentHistoryIndex(prev => ({
        ...prev,
        [section]: newIndex
      }));

      // Update Redux with new history index
      dispatch({
        type: PROPOSAL_SET_VALUES,
        payload: {
          [`${section}_history_index`]: newIndex
        }
      });

      switch (section) {
        case 'window_cleaning':
          setWindowCleaningContent(previousContent);
          dispatch({
            type: PROPOSAL_SET_VALUES,
            payload: { user_window_cleaning: previousContent }
          });
          break;
        case 'pressure_wash':
          setPressureWashingContent(previousContent);
          dispatch({
            type: PROPOSAL_SET_VALUES,
            payload: { user_template_pressure_wash: previousContent }
          });
          break;
        case 'main':
          setEditorContent(previousContent);
          dispatch({
            type: PROPOSAL_SET_VALUES,
            payload: { user_template: previousContent }
          });
          break;
      }
    }
  };

  useEffect(() => {
    if (!loading && template && !disable_fields) {
      if (currentSection === 'window_cleaning') {
        setWindowCleaningContent(template);
        addToHistory('window_cleaning', windowCleaningContent);
        dispatch({
          type: PROPOSAL_SET_VALUES,
          payload: {
            user_window_cleaning: template
          }
        });
      } else if (currentSection === 'pressure_wash') {
        setPressureWashingContent(template);
        addToHistory('pressure_wash', pressureWashingContent);
        dispatch({
          type: PROPOSAL_SET_VALUES,
          payload: {
            user_template_pressure_wash: template
          }
        });
      } else if (currentSection === 'main') {
        setEditorContent(template);
        addToHistory('main', editorContent);
        dispatch({
          type: PROPOSAL_SET_VALUES,
          payload: {
            user_template: template
          }
        });

        let value = proposalValues?.project_type || "Any Type General Cleaning";
        let select_project = projectTypes.find((projectType) => {
          return projectType.name.toLowerCase() == value.toLowerCase();
        });
        dispatch({
          type: PROJECT_TYPE_TEMPLATE_SET,
          payload: { id: select_project?.id, user_template: template }
        });
      }
    }
  }, [template]);

  useEffect(() => {
    let select_project = projectTypes.find((projectType) => {
      return projectType.name.toLowerCase() == "pressure wash";
    });
    setPressureWashingContent(
      select_project?.user_template || select_project?.template
    );
  }, [projectTypes]);

  useEffect(() => {
    if (proposalValues?.user_window_cleaning) {
      setWindowCleaningContent(proposalValues.user_window_cleaning);
    } else {
      const select_project = userProjectTypes?.find((projectType) => {
        return projectType.name.toLowerCase() == "exterior window cleaning";
      });
      const template = select_project?.user_template || proposalValues?.default_window_cleaning;
      dispatch({
        type: PROPOSAL_SET_VALUES,
        payload: { user_window_cleaning: template },
      });
    }
  }, [proposalValues?.user_window_cleaning, projectTypes]);

  useEffect(() => {
    if (proposalValues?.user_template_pressure_wash) {
      setPressureWashingContent(proposalValues?.user_template_pressure_wash);
    } else {
      let select_project = projectTypes.find((projectType) => {
        return projectType.name.toLowerCase() == "pressure wash";
      });

      setPressureWashingContent(select_project?.user_template || select_project?.template);

      dispatch({
        type: PROPOSAL_SET_VALUES,
        payload: {
          user_template_pressure_wash: select_project?.template
        },
      });
    }
  }, [proposalValues?.template_pressure_wash, proposalValues?.user_template_pressure_wash]);

  useEffect(() => {
    if (proposalValues?.user_template) {
      setEditorContent(proposalValues.user_template);
    }
  }, [proposalValues?.user_template]);

  const handleFormat = (command, value = null) => (e) => {
    e.preventDefault();
    saveSelection();
    document.execCommand(command, false, value);
    restoreSelection();
    editorRef.current?.focus();
  };

  const handleUndo = (e) => {
    e.preventDefault();
    if (editorRef.current) {
      editorRef.current.focus();
      saveSelection();
      document.execCommand('undo', false, null);
      restoreSelection();
      checkUndoRedo(editorRef, setCanUndo, setCanRedo);
    }
  };

  const handleRedo = (e) => {
    e.preventDefault();
    if (editorRef.current) {
      editorRef.current.focus();
      saveSelection();
      document.execCommand('redo', false, null);
      restoreSelection();
      checkUndoRedo(editorRef, setCanUndo, setCanRedo);
    }
  };

  const handleWindowCleaningUndo = (e) => {
    e.preventDefault();
    if (windowCleaningRef.current) {
      windowCleaningRef.current.focus();
      saveSelection();
      document.execCommand('undo', false, null);
      restoreSelection();
      checkUndoRedo(windowCleaningRef, setCanWindowUndo, setCanWindowRedo);
    }
  };

  const handleWindowCleaningRedo = (e) => {
    e.preventDefault();
    if (windowCleaningRef.current) {
      windowCleaningRef.current.focus();
      saveSelection();
      document.execCommand('redo', false, null);
      restoreSelection();
      checkUndoRedo(windowCleaningRef, setCanWindowUndo, setCanWindowRedo);
    }
  };

  const handlePressureWashUndo = (e) => {
    e.preventDefault();
    if (pressureWashRef.current) {
      pressureWashRef.current.focus();
      saveSelection();
      document.execCommand('undo', false, null);
      restoreSelection();
      checkUndoRedo(pressureWashRef, setCanPressureUndo, setCanPressureRedo);
    }
  };

  const handlePressureWashRedo = (e) => {
    e.preventDefault();
    if (pressureWashRef.current) {
      pressureWashRef.current.focus();
      saveSelection();
      document.execCommand('redo', false, null);
      restoreSelection();
      checkUndoRedo(pressureWashRef, setCanPressureUndo, setCanPressureRedo);
    }
  };

  const handleFontSize = (increment) => (e) => {
    e.preventDefault();
    saveSelection();

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;

    // Get current font size
    const currentSize = parseInt(window.getComputedStyle(element).fontSize) || 16;
    const newSize = currentSize + increment;

    // Apply the new font size directly
    element.style.fontSize = `${newSize}px`;

    editorRef.current?.focus();
  };

  const increaseFontSize = handleFontSize(1);
  const decreaseFontSize = handleFontSize(-1);

  const handleWindowCleaningFormat = (command, value = null) => (e) => {
    e.preventDefault();
    saveSelection();
    document.execCommand(command, false, value);
    restoreSelection();
    windowCleaningRef.current?.focus();
  };

  const handlePressureWashFormat = (command, value = null) => (e) => {
    e.preventDefault();
    saveSelection();
    document.execCommand(command, false, value);
    restoreSelection();
    pressureWashRef.current?.focus();
  };

  const checkUndoRedo = (ref, setUndo, setRedo) => {
    if (ref.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (ref.current.contains(range.commonAncestorContainer)) {
          setUndo(document.queryCommandEnabled('undo'));
          setRedo(document.queryCommandEnabled('redo'));
        }
      }
    }
  };

  const handleEditorFocus = () => {
    if (editorRef.current) {
      editorRef.current.focus();
      checkUndoRedo(editorRef, setCanUndo, setCanRedo);
    }
  };

  const handleWindowCleaningFocus = () => {
    if (windowCleaningRef.current) {
      windowCleaningRef.current.focus();
      checkUndoRedo(windowCleaningRef, setCanWindowUndo, setCanWindowRedo);
    }
  };

  const handlePressureWashFocus = () => {
    if (pressureWashRef.current) {
      pressureWashRef.current.focus();
      checkUndoRedo(pressureWashRef, setCanPressureUndo, setCanPressureRedo);
    }
  };

  const renderUndoButton = (section) => {
    const canUndoTemplate = currentHistoryIndex[section] > 0;
    return (
      <Button
        variant="light"
        onClick={() => handleTemplateUndo(section)}
        disabled={!canUndoTemplate}
        title="Undo AI Template"
        className="ms-2"
      >
        <FaUndo /> Undo Template
      </Button>
    );
  };

  return (
    <div className="editor-container">
      {!disable_fields &&
        <>
          {/* <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={() => handleCustomPrompt('main')}
              disabled={loading || proposalLoading || templatesLoading}
              title="Custom Prompt"
              className="fs-5 text-center"
            >
              <FaRobot className="me-2 pb-1" size={18} />
              Custom Prompt{" "}
              {loading && (
                <span
                  className="spinner-border spinner-border-sm ms-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
            </Button>
            <Button
              variant="primary"
              onClick={() => handleGenerateAiTemplate('main')}
              disabled={loading || proposalLoading || templatesLoading}
              title="Generate from AI"
              className="fs-5 text-center"
            >
              <FaRobot className="me-2 pb-1" size={18} />
              Generate from AI{" "}
              {loading && (
                <span
                  className="spinner-border spinner-border-sm ms-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
            </Button>
            {renderUndoButton('main')}
          </div> */}
          <div className="rich-text-toolbar mb-3 d-flex align-items-center">
            <div className="d-flex">
              <ButtonGroup className="me-2">
                <Button variant="light" onMouseDown={handleFormat('bold')} title="Bold">
                  <FaBold />
                </Button>
                <Button variant="light" onMouseDown={handleFormat('italic')} title="Italic">
                  <FaItalic />
                </Button>
                <Button variant="light" onMouseDown={handleFormat('underline')} title="Underline">
                  <FaUnderline />
                </Button>
              </ButtonGroup>

              <ButtonGroup className="me-2">
                <Button variant="light" onMouseDown={handleFormat('justifyLeft')} title="Align Left">
                  <FaAlignLeft />
                </Button>
                <Button variant="light" onMouseDown={handleFormat('justifyCenter')} title="Align Center">
                  <FaAlignCenter />
                </Button>
                <Button variant="light" onMouseDown={handleFormat('justifyRight')} title="Align Right">
                  <FaAlignRight />
                </Button>
              </ButtonGroup>

              <ButtonGroup className="me-2">
                <Button variant="light" onMouseDown={handleFormat('insertUnorderedList')} title="Bullet List">
                  <FaListUl />
                </Button>
                <Button variant="light" onMouseDown={handleFormat('insertOrderedList')} title="Numbered List">
                  <FaListOl />
                </Button>
              </ButtonGroup>

              <ButtonGroup>
                <Button
                  variant="light"
                  onMouseDown={handleUndo}
                  title="Undo"
                  disabled={!canUndo}
                >
                  <FaUndo />
                </Button>
                <Button
                  variant="light"
                  onMouseDown={handleRedo}
                  title="Redo"
                  disabled={!canRedo}
                >
                  <FaRedo />
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </>
      }

      <div
        ref={editorRef}
        style={{ textTransform: "initial", fontSize: "15px", fontFamily: 'Arial' }}
        className="editor"
        contentEditable={true}
        dangerouslySetInnerHTML={{ __html: editorContent }}
        onBlur={handleEditorChange}
        onFocus={handleEditorFocus}
        onInput={handleEditorFocus}
      ></div>

      {sections.window_cleaning && (
        <>
          <br />
          {!disable_fields &&
            <>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => handleCustomPrompt('window_cleaning')}
                  disabled={loading || proposalLoading || templatesLoading}
                  title="Custom Prompt"
                  className="fs-5 text-center d-flex align-items-center"
                >
                  <FaRobot className="me-2 pb-1" size={18} />
                  Custom Prompt{" "}
                  {loading && (
                    <span
                      className="spinner-border spinner-border-sm ms-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleGenerateAiTemplate('window_cleaning')}
                  disabled={loading || proposalLoading || templatesLoading}
                  title="Generate from AI"
                  className="fs-5 text-center d-flex align-items-center"
                >
                  <FaRobot className="me-2 pb-1" size={18} />
                  Generate from AI{" "}
                  {loading && (
                    <span
                      className="spinner-border spinner-border-sm ms-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                </Button>
                {renderUndoButton('window_cleaning')}
              </div>
              <div className="rich-text-toolbar mb-3 d-flex align-items-center">
                <div className="d-flex">
                  <ButtonGroup className="me-2">
                    <Button variant="light" onMouseDown={handleWindowCleaningFormat('bold')} title="Bold">
                      <FaBold />
                    </Button>
                    <Button variant="light" onMouseDown={handleWindowCleaningFormat('italic')} title="Italic">
                      <FaItalic />
                    </Button>
                    <Button variant="light" onMouseDown={handleWindowCleaningFormat('underline')} title="Underline">
                      <FaUnderline />
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup className="me-2">
                    <Button variant="light" onMouseDown={handleWindowCleaningFormat('justifyLeft')} title="Align Left">
                      <FaAlignLeft />
                    </Button>
                    <Button variant="light" onMouseDown={handleWindowCleaningFormat('justifyCenter')} title="Align Center">
                      <FaAlignCenter />
                    </Button>
                    <Button variant="light" onMouseDown={handleWindowCleaningFormat('justifyRight')} title="Align Right">
                      <FaAlignRight />
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup className="me-2">
                    <Button variant="light" onMouseDown={handleWindowCleaningFormat('insertUnorderedList')} title="Bullet List">
                      <FaListUl />
                    </Button>
                    <Button variant="light" onMouseDown={handleWindowCleaningFormat('insertOrderedList')} title="Numbered List">
                      <FaListOl />
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup>
                    <Button
                      variant="light"
                      onMouseDown={handleWindowCleaningUndo}
                      title="Undo"
                      disabled={!canWindowUndo}
                    >
                      <FaUndo />
                    </Button>
                    <Button
                      variant="light"
                      onMouseDown={handleWindowCleaningRedo}
                      title="Redo"
                      disabled={!canWindowRedo}
                    >
                      <FaRedo />
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            </>
          }
          <div
            ref={windowCleaningRef}
            style={{ textTransform: "initial", fontSize: "15px", fontFamily: 'Arial' }}
            className="editor"
            contentEditable={true}
            dangerouslySetInnerHTML={{ __html: windowCleaningContent }}
            onBlur={handleWindowCleaningChange}
            onFocus={handleWindowCleaningFocus}
            onInput={handleWindowCleaningFocus}
          ></div>
        </>
      )}
      {sections.pressure_washing &&
        proposalValues?.project_type?.toLowerCase() !== "pressure wash" && (
          <>
            <br />
            {!disable_fields &&
              <>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => handleCustomPrompt('pressure_wash')}
                    disabled={loading || proposalLoading || templatesLoading}
                    title="Custom Prompt"
                    className="fs-5 text-center d-flex align-items-center"
                  >
                    <FaRobot className="me-2 pb-1" size={18} />
                    Custom Prompt{" "}
                    {loading && (
                      <span
                        className="spinner-border spinner-border-sm ms-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    )}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleGenerateAiTemplate('pressure_wash')}
                    disabled={loading || proposalLoading || templatesLoading}
                    title="Generate from AI"
                    className="fs-5 text-center d-flex align-items-center"
                  >
                    <FaRobot className="me-2 pb-1" size={18} />
                    Generate from AI{" "}
                    {loading && (
                      <span
                        className="spinner-border spinner-border-sm ms-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    )}
                  </Button>
                  {renderUndoButton('pressure_wash')}
                </div>
                <div className="rich-text-toolbar mb-3 d-flex align-items-center">
                  <div className="d-flex">
                    <ButtonGroup className="me-2">
                      <Button variant="light" onMouseDown={handlePressureWashFormat('bold')} title="Bold">
                        <FaBold />
                      </Button>
                      <Button variant="light" onMouseDown={handlePressureWashFormat('italic')} title="Italic">
                        <FaItalic />
                      </Button>
                      <Button variant="light" onMouseDown={handlePressureWashFormat('underline')} title="Underline">
                        <FaUnderline />
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup className="me-2">
                      <Button variant="light" onMouseDown={handlePressureWashFormat('justifyLeft')} title="Align Left">
                        <FaAlignLeft />
                      </Button>
                      <Button variant="light" onMouseDown={handlePressureWashFormat('justifyCenter')} title="Align Center">
                        <FaAlignCenter />
                      </Button>
                      <Button variant="light" onMouseDown={handlePressureWashFormat('justifyRight')} title="Align Right">
                        <FaAlignRight />
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup className="me-2">
                      <Button variant="light" onMouseDown={handlePressureWashFormat('insertUnorderedList')} title="Bullet List">
                        <FaListUl />
                      </Button>
                      <Button variant="light" onMouseDown={handlePressureWashFormat('insertOrderedList')} title="Numbered List">
                        <FaListOl />
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup>
                      <Button
                        variant="light"
                        onMouseDown={handlePressureWashUndo}
                        title="Undo"
                        disabled={!canPressureUndo}
                      >
                        <FaUndo />
                      </Button>
                      <Button
                        variant="light"
                        onMouseDown={handlePressureWashRedo}
                        title="Redo"
                        disabled={!canPressureRedo}
                      >
                        <FaRedo />
                      </Button>
                    </ButtonGroup>
                  </div>
                </div>
              </>
            }
            <div
              ref={pressureWashRef}
              style={{ textTransform: "initial", fontSize: "15px", fontFamily: 'Arial' }}
              className="editor"
              contentEditable={true}
              dangerouslySetInnerHTML={{ __html: pressureWashingContent }}
              onBlur={handlePressureWashChange}
              onFocus={handlePressureWashFocus}
              onInput={handlePressureWashFocus}
            ></div>
          </>
        )}

      <CustomPromptModal
        show={showCustomPrompt}
        onHide={() => setShowCustomPrompt(false)}
        customPrompt={customPrompt}
        setCustomPrompt={setCustomPrompt}
        onSave={handleSaveCustomPrompt}
        loading={loading}
        section={currentSection}
      />
    </div>
  );
};

export default SimpleRichTextEditor;
